import { useEffect, useState } from 'react';
import { UseDocumentDetailsProps } from '../types';
import { AIDocumentContractModelWithId } from '../../../models/ai.document.contract.model';
import { DocumentVaultItemModel } from '../../../models/document.vault.item.model';
import { NWClient } from '../../../client/NWClient';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { fetchEmployees, selectAllEmployees } from '../../../slices/employees/employeesSlice';
import { fetchSuppliers, selectAllSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { fetchCustomers, selectAllCustomers } from '../../../slices/customers/customersSlice';
import {
  fetchBusinessProfileContactById,
  selectBusinessProfileContacts,
} from '../../../slices/business-profile/businessProfileSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { selectAllCompanies } from '../../../slices/companies/companiesSlice';
import { TAGS_LABELS } from '../../../common/constants/tags-labels';
import { selectUser } from '../../../slices/user/userSlice';
import { Helpers } from '../../../common/helpers/helpers';
import { fetchDocumentUploadHTML } from '../../../slices/document-upload-html/documentUploadHTMLSlice';
import dayjs, { Dayjs } from 'dayjs';
import { resetCurrentDocumentAISummary } from '../../../slices/current-document-ai-summary/currentDocumentAISummarySlice';
import { useSearchParams } from 'react-router-dom';

export const useDocumentDetails = ({
  id,
  isDocumentContract,
  allowedCreatingHTML,
  allowedGettingVersions,
}: UseDocumentDetailsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const company = useAppSelector(selectAllCompanies)[0];
  const employees = useAppSelector(selectAllEmployees);
  const suppliers = useAppSelector(selectAllSuppliers);
  const customers = useAppSelector(selectAllCustomers);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const [params] = useSearchParams();
  const generateTemplate = params.get('generateTemplate');
  const editDocumentParam = params.get('editDocument');
  const isDocumentGenerated = params.get('contractGenerated');
  const [documentDetails, setDocumentDetails] = useState<
    AIDocumentContractModelWithId & DocumentVaultItemModel
  >(null);
  const [documentUploadLatestHTMLId, setDocumentUploadLatestHTMLId] = useState<number>(null);
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [documentUploadHTMLAttempted, setDocumentUploadHTMLAttempted] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [documentTypeLabel, setDocumentTypeLabel] = useState<string>('');
  const [documentWorkflowLabel, setDocumentWorkflowLabel] = useState<string>('');
  const [assigneeLabel, setAssigneeLabel] = useState<string>('');
  const [propertiesEditMode, setPropertiesEditMode] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [subscription, setSubscription] = useState<string>('');
  const [signed, setSigned] = useState<string>('');
  const [frequency, setFrequency] = useState<string>('');
  const [customLabel, setCustomLabel] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [currency, setCurrency] = useState<string>('aud');
  const [htmlRetrieved, setHTMLRetrieved] = useState<boolean>(false);
  const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
  const [pdfDocHTMLCreationAllowed, setPDFDocHTMLCreationAllowed] = useState<boolean>(false);

  const currencyValuesArray = [
    { label: t('labels.aud'), value: 'aud' },
    { label: t('labels.eur'), value: 'eur' },
    { label: t('labels.gbp'), value: 'gbp' },
    { label: t('labels.usd'), value: 'usd' },
  ];

  const booleanValuesArray = [
    { label: t('labels.yes'), value: 'true' },
    { label: t('labels.no'), value: 'false' },
  ];

  const frequencyValuesArray = [
    { label: t('labels.hourly'), value: 'hourly' },
    { label: t('labels.weekly'), value: 'weekly' },
    { label: t('labels.biweekly'), value: 'biweekly' },
    { label: t('labels.monthly'), value: 'monthly' },
    { label: t('labels.yearly'), value: 'yearly' },
  ];

  const documentTypeLabels = TAGS_LABELS.documentType.map((el) => t(el));
  const getTypeLabels = () => {
    return documentTypeLabels.map((el) => ({ label: el, value: el }));
  };

  const documentWorkflowLabelsEmployment = TAGS_LABELS.documentWorkflow.employment.map((el) =>
    t(el)
  );
  const documentWorkflowLabelsEmploymentSorted = documentWorkflowLabelsEmployment.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsSupplier = TAGS_LABELS.documentWorkflow.supplier.map((el) => t(el));
  const documentWorkflowLabelsSupplierSorted = documentWorkflowLabelsSupplier.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsCustomer = TAGS_LABELS.documentWorkflow.customer.map((el) => t(el));
  const documentWorkflowLabelsCustomerSorted = documentWorkflowLabelsCustomer.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsBusiness = TAGS_LABELS.documentWorkflow.business.map((el) => t(el));
  const documentWorkflowLabelsBusinessSorted = documentWorkflowLabelsBusiness.sort((a, b) =>
    a.localeCompare(b)
  );

  const employeesLabels = employees
    .map((el) => ({
      label: `${el.full_name}`,
      value: String(el.id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const suppliersLabels = suppliers
    .map((el) => ({
      label: el.name,
      value: String(el.id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const customersLabels = customers
    .map((el) => ({
      label: el.customer_name,
      value: String(el.id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const businessContactsLabels = businessContacts
    .map((el) => ({
      label: el.contact_name,
      value: String(el.id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const categoryArray = ['Business', 'Customers', 'Employment', 'Suppliers'];

  const categorySelectValues = categoryArray.map((category) => ({
    value: category,
    label: category,
  }));

  const profileLabelsArray = (categoryParam?: string) => {
    const categoryValue =
      categoryParam ||
      category ||
      (documentDetails?.category ? documentDetails.category.toLowerCase() : '');

    if (categoryValue) {
      switch (categoryValue.toLowerCase()) {
        case 'employment':
          return employeesLabels;
        case 'suppliers':
          return suppliersLabels;
        case 'customers':
          return customersLabels;
        case 'business':
          return businessContactsLabels;
        default:
          return [];
      }
    } else {
      return [];
    }
  };

  useEffect(() => {
    if (company && category && !loadingData) {
      const lowerCaseCategory = category.toLowerCase();

      if (lowerCaseCategory === 'employment') {
        dispatch(fetchEmployees({ company_id: company.id }));
      } else if (lowerCaseCategory === 'customers') {
        dispatch(fetchCustomers({ company_id: company.id }));
      } else if (lowerCaseCategory === 'suppliers') {
        dispatch(fetchSuppliers({ company_id: company.id }));
      } else if (lowerCaseCategory === 'business') {
        dispatch(fetchBusinessProfileContactById({ id: company.id }));
      }
    }
  }, [category, company]);

  const profileLabelsProp = (category?: string) => {
    const categoryValue = category || documentDetails?.category;
    if (categoryValue) {
      switch (categoryValue) {
        case 'employment':
          return 'employees';
        case 'suppliers':
          return 'suppliers';
        case 'customers':
          return 'customers';
        case 'business':
          return 'business_profile_contacts';
      }
    }
  };

  const workflowsArray = () => {
    // Use the current category state rather than documentDetails.category
    const currentCategory = category
      ? category.toLowerCase()
      : documentDetails?.category
      ? documentDetails.category
      : '';

    switch (currentCategory) {
      case 'employment':
        return documentWorkflowLabelsEmploymentSorted.map((el) => ({ label: el, value: el }));
      case 'suppliers':
        return documentWorkflowLabelsSupplierSorted.map((el) => ({ label: el, value: el }));
      case 'customers':
        return documentWorkflowLabelsCustomerSorted.map((el) => ({ label: el, value: el }));
      case 'business':
        return documentWorkflowLabelsBusinessSorted.map((el) => ({ label: el, value: el }));
      default:
        return [];
    }
  };

  const createDocumentHTML = (documentId: number) => {
    if (!pdfDocHTMLCreationAllowed) {
      setLoadingData(true);
    }
    NWClient.post('document-upload-html', { document_upload: documentId })
      .then((res: { id: number }) => {
        localStorage.setItem('firstConversionFail', 'false');
        setDocumentUploadLatestHTMLId(res.id);
        setHTMLRetrieved(true);
        dispatch(fetchDocumentUploadHTML({ data: { document_upload: Number(id) } }));
      })
      .catch((error) => {
        if (localStorage.getItem('firstConversionFail') === 'true') {
          toast.error(error.message || t('messages.errorOccurred'));
        } else {
          localStorage.setItem('firstConversionFail', 'true');
          setErrorModalOpen(true);
        }
      })
      .finally(() => {
        setLoadingData(false);
        setPDFDocHTMLCreationAllowed(false);
      });
  };

  const updateDocumentItem = () => {
    setPendingStatus(true);
    const updatePromise1 = NWClient.patch(
      isDocumentGenerated ? 'tag-document-contract' : 'tag-document-upload',
      id,
      {
        [isDocumentGenerated ? 'dc_types' : 'du_types']: documentTypeLabel
          ? [documentTypeLabel]
          : [],
        [isDocumentGenerated ? 'dc_workflows' : 'du_workflows']: documentWorkflowLabel
          ? [documentWorkflowLabel]
          : [],
        [isDocumentGenerated ? 'dc_label' : 'du_label']: customLabel ? [customLabel] : [],
      }
    );
    const sentAmount = Number(amount);
    const updatePromise2 = NWClient.patch(
      `${isDocumentGenerated ? 'document-contract' : 'document-upload'}/${id}/`,
      null,
      {
        [profileLabelsProp(category.toLowerCase())]: assigneeLabel ? [Number(assigneeLabel)] : [],
        category: category ? category.toLowerCase() : '',
        extras: {
          start_date: startDate ? startDate.toISOString().substring(0, 10) : null,
          end_date: endDate ? endDate.toISOString().substring(0, 10) : null,
          is_signed: signed === 'true',
          subscription: subscription === 'true',
          frequency,
          amount: sentAmount,
          currency,
        },
        name: fileName,
        user: currentUser.id,
      }
    );
    Promise.all([updatePromise1, updatePromise2])
      .then(() => {
        toast.success(t('messages.documentUpdated'));
      })
      .finally(() => {
        setPendingStatus(false);
      });
  };

  useEffect(() => {
    if (id) {
      NWClient.get(isDocumentGenerated ? 'document-contract' : 'document-upload', id).then(
        (res: DocumentVaultItemModel) => {
          const fileName =
            res.name.endsWith('.pdf') || res.name.endsWith('.docx') || res.name.endsWith('.doc')
              ? res.name.replace('.pdf', '').replace('.docx', '').replace('.doc', '')
              : res.name;
          setFileName(fileName);
          setDocumentDetails(res);
          setCategory(Helpers.capitalizeFirstLetter(res.category));
          if (res[isDocumentGenerated ? 'dc_workflows' : 'du_workflows'].length > 0) {
            setDocumentWorkflowLabel(
              res[isDocumentGenerated ? 'dc_workflows' : 'du_workflows'].map((el) => el)[0]
            );
          }
          if (res[isDocumentGenerated ? 'dc_types' : 'du_types'].length > 0) {
            setDocumentTypeLabel(
              res[isDocumentGenerated ? 'dc_types' : 'du_types'].map((el) => el)[0]
            );
          }
          if (res[isDocumentGenerated ? 'dc_label' : 'du_label'].length > 0) {
            setCustomLabel(res[isDocumentGenerated ? 'dc_label' : 'du_label'].map((el) => el)[0]);
          }
          setFrequency(res.extras.length > 0 ? res.extras[0].frequency : '');
          setSigned(
            res.extras.length > 0 && typeof res.extras[0].is_signed === 'boolean'
              ? res.extras[0].is_signed
                ? 'true'
                : 'false'
              : ''
          );
          setSubscription(
            res.extras.length > 0 && typeof res.extras[0].subscription === 'boolean'
              ? res.extras[0].subscription
                ? 'true'
                : 'false'
              : ''
          );
          setStartDate(
            res.extras.length > 0 && res.extras[0].start_date
              ? dayjs(new Date(res.extras[0].start_date + 'T00:00:00.000Z'))
              : null
          );
          setEndDate(
            res.extras.length > 0 && res.extras[0].end_date
              ? dayjs(new Date(res.extras[0].end_date + 'T00:00:00.000Z'))
              : null
          );
          setCurrency(res.extras.length > 0 ? res.extras[0].currency : 'aud');
          setAmount(
            res.extras.length > 0 && res.extras[0].amount !== '0' ? res.extras[0].amount : ''
          );
        }
      );
    }
    return () => {
      dispatch(resetCurrentDocumentAISummary());
    };
  }, [id]);

  useEffect(() => {
    if (
      documentDetails &&
      'document_upload_html' in documentDetails &&
      documentDetails.document_upload_html.length === 0 &&
      ('du_filetype' in documentDetails && documentDetails.du_filetype[0] === 'pdf'
        ? pdfDocHTMLCreationAllowed
        : true) &&
      allowedCreatingHTML
    ) {
      if (!documentUploadHTMLAttempted) {
        setDocumentUploadHTMLAttempted(true);
        createDocumentHTML(id);
      }
    } else {
      if (documentDetails) {
        setLoadingData(false);
        setHTMLRetrieved(true);
      }
    }
  }, [
    documentDetails,
    documentUploadHTMLAttempted,
    allowedCreatingHTML,
    pdfDocHTMLCreationAllowed,
  ]);

  useEffect(() => {
    if (company && documentDetails && !loadingData && !allowedCreatingHTML) {
      if (documentDetails.category === 'employment') {
        dispatch(fetchEmployees({ company_id: company.id }));
      } else if (documentDetails.category === 'customers') {
        dispatch(fetchCustomers({ company_id: company.id }));
      } else if (documentDetails.category === 'suppliers') {
        dispatch(fetchSuppliers({ company_id: company.id }));
      } else if (documentDetails.category === 'business') {
        dispatch(fetchBusinessProfileContactById({ id: company.id }));
      }
    }
  }, [documentDetails, company, loadingData]);

  useEffect(() => {
    if (
      documentDetails &&
      documentDetails[profileLabelsProp()].length > 0 &&
      profileLabelsArray().length > 0
    ) {
      setAssigneeLabel(
        documentDetails[profileLabelsProp()].map((el) =>
          profileLabelsArray()?.find((item) => Number(item.value) === el)
        )[0]?.value
      );
    }
  }, [documentDetails, profileLabelsArray().length, category]);

  useEffect(() => {
    if (
      (generateTemplate || editDocumentParam) &&
      documentDetails &&
      'document_upload_html' in documentDetails &&
      documentDetails.document_upload_html.length === 0 &&
      'du_filetype' in documentDetails &&
      documentDetails.du_filetype[0] === 'pdf' &&
      allowedCreatingHTML
    ) {
      setPDFDocHTMLCreationAllowed(true);
    }
  }, [documentDetails, generateTemplate, allowedCreatingHTML, editDocumentParam]);

  return {
    documentDetails,
    pendingStatus,
    setPendingStatus,
    createDocumentHTML,
    documentUploadLatestHTMLId,
    fileName,
    setFileName,
    profileLabelsArray,
    workflowsArray,
    getTypeLabels,
    categorySelectValues,
    propertiesEditMode,
    setPropertiesEditMode,
    documentTypeLabel,
    setDocumentTypeLabel,
    documentWorkflowLabel,
    setDocumentWorkflowLabel,
    assigneeLabel,
    setAssigneeLabel,
    category,
    setCategory,
    updateDocumentItem,
    loadingData,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    signed,
    setSigned,
    frequency,
    setFrequency,
    subscription,
    setSubscription,
    setCustomLabel,
    customLabel,
    amount,
    setAmount,
    currency,
    setCurrency,
    booleanValuesArray,
    frequencyValuesArray,
    currencyValuesArray,
    htmlRetrieved,
    errorModalOpen,
    setErrorModalOpen,
    pdfDocHTMLCreationAllowed,
    setPDFDocHTMLCreationAllowed,
  };
};
